// Render-side MIRROR of common-lib `stripLeakedFraming` (which runs persist-side in the harness's
// gateway ingestion). The harness now strips its own in-band framing before a row is stored, so this is
// the catch-all for legacy history and the live-stream flash — the same role the `<voice>` strip plays.
// Kept 1:1 with the server, iOS `ChatText.stripLeakedFraming`, and Android `ChatText.stripLeakedFraming`;
// the shared `common-lib/fixtures/chat-render-contract.json` guards the three clients against drift.
//
// Not imported directly from common-lib on purpose: that module (`gatewayMapping`) pulls in server-side
// mapping code we don't want in the web bundle, and each client already keeps its own copy of these
// render-time strips.

// `<metadata>…</metadata>` / `<system>…</system>` block — the platform's own framing. Both reach the
// model as tagged text inside a turn, and anything the model reads it can echo (or, for `<system>`,
// fabricate — a real turn opened with an invented `<system>[AGENT REPORT, SUCCESS] …`). Non-greedy, with
// a back-referenced close tag so the block ends at its OWN `</metadata>`/`</system>`.
const FRAMING_BLOCK = /[ \t]*<(metadata|system)>[\s\S]*?<\/\1>[ \t]*/g;

// The same wrapper closed with the WRONG tag, occupying the rest of its own line — `<metadata>{…}</parameter>`,
// a tool-call frame's closer bleeding into the text. `FRAMING_BLOCK`'s backreference needs a matching closer,
// so these survived it and are the shape stored history actually carries: one row opened a turn with a
// mis-closed `<system>` nudge, another answered `הנה!` and then suffixed the attachment metadata it had been
// reading on its own prior replies. Start-of-line only (head or mid-reply), and confined to that ONE line, so
// it can neither take a mid-paragraph mention nor cross into the answer to reach a `</div>` the reply quotes.
// A closer is required: an opener with nothing closing it stays here, which is what keeps the settled render
// at server parity — the streaming path below is what handles a partial mid-stream.
const FRAMING_MANGLED_LINE = /(?:^|\n)[ \t]*<(?:metadata|system)>[^\n]*?<\/[a-zA-Z][\w-]*>[ \t]*/g;

// A bracketed turn marker on a line of ITS OWN — `[AGENT REPORT …]`, `[TASK COMPLETED …]`, etc. Anchored
// to a whole line (the `m` flag) so a sentence that merely mentions one mid-paragraph is left alone.
const FRAMING_MARKER_LINE =
  /^[ \t]*\[(?:AGENT REPORT|TASK COMPLETED|TASK BLOCKED|SUB-AGENT|NUDGE FROM USER|sub-agent conversation_id|run cost)\b[^\]\n]*\][ \t]*$\n?/gim;

// Strip the platform's own in-band framing out of model-emitted text. Removes the `<metadata>`/`<system>`
// blocks AND their content (unlike `stripVoiceTags`, which keeps the inner words), plus the bracketed
// turn-marker lines, collapses the blank runs a removal leaves, and trims. Deliberately narrow — only the
// exact wrappers we inject — so it can't eat a user-facing sentence; idempotent, and text with no framing
// returns unchanged (the fast-path guard).
export function stripLeakedFraming(text: string): string {
  if (!text.includes('<metadata>') && !text.includes('<system>') && !text.includes('[')) {
    return text;
  }
  return text
    .replace(FRAMING_BLOCK, '')
    .replace(FRAMING_MANGLED_LINE, '')
    .replace(FRAMING_MARKER_LINE, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

// An UNCLOSED trailing framing open tag: a lowercase `<metadata>`/`<system>` with no matching close
// (case-sensitive, like the block).
const UNCLOSED_FRAMING_TAIL = /<(metadata|system)>[\s\S]*$/;

// STREAMING-ONLY strip. After the balanced blocks are gone (`stripLeakedFraming`), a live reply can still
// hold an OPENING `<system>`/`<metadata>` whose closing tag hasn't streamed yet — the fabricated
// `<system>[AGENT REPORT …]` opening a turn. Remove that unclosed tag and everything after it so the
// partial never flashes; once the close arrives the balanced strip handles it identically. Apply ONLY on
// the streaming render path, never to settled text — so a genuinely-unclosed `<system>` in stored history
// keeps exact server parity. Mirrors iOS/Android `stripStreamingPartialFraming`.
export function stripStreamingLeakedFraming(text: string): string {
  return stripLeakedFraming(text).replace(UNCLOSED_FRAMING_TAIL, '').replace(/[ \t\n]+$/, '');
}
